import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import ErrorIcon from '@mui/icons-material/Error'
import PreviewIcon from '@mui/icons-material/Preview'
import SyncIcon from '@mui/icons-material/Sync'
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useDealData } from 'api/aws/deals/useDealData'
import { baseUrl } from 'api/config'
import AccentBarPanel from 'theme/components/PageHeader/AccentBarPanel'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'

interface SyncPreview {
  account: {
    name: string
    type: string
    external_id: string
    website_url?: string | null
    city?: string | null
    state?: string | null
  }
  contacts: Array<{
    name: string
    email: string | null
    role: string | null
  }>
  subscriptions: Array<{
    name: string
    service_line_code: string
    price: number
    quantity: number
    status: string
    start_date: string | null
    end_date: string | null
  }>
  metadata: {
    deal_name: string
    deal_type: string
    total: number
    monthly_total: number
    gross_profit: number
  }
}

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
)

const FieldRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.4, minHeight: 28 }}>
    <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
      {value || '—'}
    </Typography>
  </Box>
)

const StatCard = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => {
  const theme = useTheme()
  return (
  <Box
    sx={{
      textAlign: 'center',
      py: 1,
      px: 1,
      borderRadius: 1.5,
      bgcolor: highlight ? alpha(theme.palette.text.highlight, 0.08) : alpha(theme.palette.text.primary, 0.03),
      border: '1px solid',
      borderColor: highlight ? alpha(theme.palette.text.highlight, 0.2) : 'divider',
      flex: 1,
    }}
  >
    <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: highlight ? 'primary.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
  )
}

const DealHeartbeatSync = () => {
  const { PK, SK } = useParams()
  const dealId = SK?.replace('DEAL_', '') || ''
  const { deal, organization } = useDealData(PK, SK, dealId)
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [preview, setPreview] = useState<SyncPreview | null>(null)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const isSynced = deal?.heartbeat_synced === true
  const syncedAt = deal?.heartbeat_synced_at
  const heartbeatAccountId = organization?.heartbeat_account_id

  const handlePreview = useCallback(async () => {
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch(`${baseUrl}/aws/heartbeat/preview?PK=${encodeURIComponent(PK || '')}&dealSK=${encodeURIComponent(SK || '')}`)
      const data = await res.json()
      if (data.success) {
        setPreview(data.payload)
      } else {
        setError(data.error || 'Failed to generate preview')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [PK, SK])

  const handleSync = useCallback(async () => {
    setSyncing(true)
    setError(null)
    try {
      const res = await fetch(`${baseUrl}/aws/heartbeat/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PK, dealSK: SK }),
      })
      const data = await res.json()
      if (data.success) {
        setResult(data.data)
        setPreview(null)
      } else {
        setError(data.error || 'Sync failed')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSyncing(false)
    }
  }, [PK, SK])

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)

  const buttonSx = {
    textTransform: 'none' as const,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 2,
    px: 2,
    py: 0.75,
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <AccentBarPanel
        title="Heartbeat Integration"
        text="Push this deal's data to Heartbeat to create or update the account, contacts, and subscription lines."
      />

      {/* Status Chips */}
      <Stack direction="row" spacing={1} mt={2} mb={2} flexWrap="wrap">
        {isSynced && (
          <Chip
            icon={<CheckCircleIcon />}
            label={`Synced${syncedAt ? ` ${new Date(syncedAt).toLocaleDateString()}` : ''}`}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.text.complete, 0.12),
              color: theme.palette.text.complete,
              borderColor: alpha(theme.palette.text.complete, 0.3),
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        )}
        {heartbeatAccountId && (
          <Chip
            label={`Account #${heartbeatAccountId}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, fontWeight: 600 }}
          />
        )}
        {deal?.stage === 'Deal Won' && (
          <Chip
            label="Auto-syncs on Deal Won"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.text.link, 0.1),
              color: theme.palette.text.link,
              fontSize: 11,
              fontWeight: 600,
            }}
          />
        )}
      </Stack>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1.5} mb={2.5}>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <PreviewIcon sx={{ fontSize: '18px !important' }} />}
          onClick={handlePreview}
          disabled={loading || syncing}
          sx={{
            ...buttonSx,
            borderColor: alpha(theme.palette.text.primary, 0.15),
            '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.text.link, 0.06) },
          }}
        >
          Preview
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon sx={{ fontSize: '18px !important' }} />}
          onClick={handleSync}
          disabled={loading || syncing}
          sx={buttonSx}
        >
          {isSynced ? 'Re-sync' : 'Sync'} to Heartbeat
        </Button>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setResult(null)}>
          Synced successfully — Account #{result.account_id}, {result.subscriptions_synced} subscription(s),{' '}
          {result.contacts_synced} contact(s)
        </Alert>
      )}

      {/* Preview Panel */}
      <Collapse in={!!preview}>
        {preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Account Section */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
              <SectionLabel>Account</SectionLabel>
              <FieldRow label="Name" value={preview.account.name} />
              <FieldRow label="Type" value={preview.account.type} />
              {preview.account.city && (
                <FieldRow label="Location" value={`${preview.account.city}, ${preview.account.state}`} />
              )}
            </Box>

            {/* Subscriptions Section */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                <SectionLabel>Subscriptions ({preview.subscriptions.length})</SectionLabel>
              </Box>
              {/* Subscription header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 100px 100px 80px 140px',
                  px: 1.5,
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.text.primary, 0.02),
                }}
              >
                {['Product', 'Service', 'Price', 'Status', 'Dates'].map(h => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'text.secondary',
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              {/* Subscription rows */}
              {preview.subscriptions.map((sub, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 100px 100px 80px 140px',
                    px: 1.5,
                    py: 0.6,
                    borderBottom: i < preview.subscriptions.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 500 }} noWrap>{sub.name}</Typography>
                  <Chip label={sub.service_line_code} size="small" variant="outlined" sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: theme.palette.text.complete }}>
                    {formatCurrency(sub.price)}
                  </Typography>
                  <Chip
                    label={sub.status}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      fontWeight: 600,
                      bgcolor: sub.status === 'active' ? alpha(theme.palette.text.complete, 0.12) : alpha(theme.palette.text.primary, 0.05),
                      color: sub.status === 'active' ? theme.palette.text.complete : 'text.secondary',
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {sub.start_date || '—'} → {sub.end_date || '—'}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Contacts Section */}
            {preview.contacts.length > 0 && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                <SectionLabel>Contacts ({preview.contacts.length})</SectionLabel>
                {preview.contacts.map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{c.name}</Typography>
                    {c.email && (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>— {c.email}</Typography>
                    )}
                    {c.role && (
                      <Chip label={c.role} size="small" variant="outlined" sx={{ height: 18, fontSize: 9, fontWeight: 600, ml: 'auto' }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Totals Stats */}
            <Stack direction="row" spacing={1.5}>
              <StatCard label="Monthly Total" value={formatCurrency(preview.metadata.monthly_total)} />
              <StatCard label="Deal Total" value={formatCurrency(preview.metadata.total)} highlight />
              <StatCard label="Gross Profit" value={formatCurrency(preview.metadata.gross_profit)} />
            </Stack>
          </Box>
        )}
      </Collapse>
    </Box>
  )
}

export default DealHeartbeatSync
